import { useCallback, useState } from 'react';
import { buttonClick, RESUME_STEPS, RESUME_TYPE, sendResumptionEvent } from '@belong/analytics';
import { getPersistedOrder, setPersistedOrder, useNbnCartChange } from '@belong/nbn-utils';
import useAnalyticsEvents from '@belong/providers/analyticsEvents';
import { COLOURS } from '@belong/themes';
import { IElementSystemIcon } from '@belong/types';
import { urls } from '@belong/url-utils';
import { IHeaderBarLink } from '../GlobalHeaderDesktop/HeaderBarButton';
import { DesktopNbnCartButton } from './DesktopNbnCartButton';
import { MobileCartButtonWrapper, StyledIconWithDot } from './styles';

export interface INbnCartButton extends IHeaderBarLink {
  a11yLabel: string;
  cartIcon: IElementSystemIcon;
  isNbnOrderCartReady?: boolean;
  isDesktop?: boolean;
  menuLabel: string;
}

export const NbnCartButton = ({
  a11yLabel,
  cartIcon,
  isDesktop = false,
  isNbnOrderCartReady = false,
  menuLabel,
  ...otherProps
}: INbnCartButton) => {
  const [showCartButton, setShowCartButton] = useState(isNbnOrderCartReady);
  const analyticsEvents = useAnalyticsEvents();

  const hideCartButton = useCallback(() => {
    setShowCartButton(false);
  }, []);

  useNbnCartChange(hideCartButton);

  if (!showCartButton) {
    return null;
  }

  const handleCartClick = () => {
    const persistedData = getPersistedOrder();

    const resumeAnalytics = sendResumptionEvent(analyticsEvents, {
      ...buttonClick({
        text: menuLabel,
        label: a11yLabel
      }),
      expireDate: persistedData.expireDate,
      previousAttemptNumber: persistedData.resumeAnalytics?.attemptNumber,
      resumeFromLastStep: RESUME_STEPS.NBN_CART,
      resumeType: RESUME_TYPE.CLIENT
    });

    setPersistedOrder({
      ...persistedData,
      resumeAnalytics
    });
  };

  const cartProps = {
    ...otherProps,
    'aria-label': a11yLabel,
    href: urls.NBN_CART,
    onClick: handleCartClick
  };

  if (isDesktop) {
    return <DesktopNbnCartButton {...cartProps} iconName={cartIcon.icon} menuLabel={menuLabel} />;
  }

  return (
    <MobileCartButtonWrapper {...cartProps}>
      <StyledIconWithDot name={cartIcon.icon} hasColor={COLOURS.BELONG_BLUE} />
    </MobileCartButtonWrapper>
  );
};

NbnCartButton.displayName = 'NbnCartButton';
